import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Battery, Gauge, Timer, Ruler, Weight, Shield, Check, ChevronLeft, ChevronRight, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingModal from "@/components/BookingModal";
import CookieConsent from "@/components/CookieConsent";
import api from "@/lib/api";

const PRODUCTS = {
    small: {
        name: "Birò Small",
        subtitle: "",
        tagline: "Mopedklass – inget B-körkort krävs",
        description: "Birò Small är vår kompakta modell. Smidig och perfekt för stadskörning. Med en räckvidd på upp till 100 km och en topphastighet på 45 km/h tar du dig enkelt genom trafiken.",
        images: [
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/2pb5q7d8_biro-classic-small-b2211_1%20%281%29.jpg",
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/6mhxvt8g_biro-classic-small-b2211_2%20%281%29.jpg",
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/ei8pzebw_biro-classic-small-b2211_3%20%281%29.jpg",
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/x68rxlaa_biro-classic-small-b2211_6.jpg",
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/93h0qhy7_biro-classic-small-b2211_7.jpg",
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/ynvav0cp_biro-classic-small-b2211_8.jpg",
        ],
        specs: [
            { icon: Gauge, label: "Topphastighet", value: "45 km/h" },
            { icon: Battery, label: "Räckvidd", value: "Upp till 100 km" },
            { icon: Timer, label: "Laddtid", value: "3–5 timmar" },
            { icon: Ruler, label: "Längd / Bredd", value: "165 / 103 cm" },
            { icon: Weight, label: "Vikt", value: "Ca 280 kg" },
            { icon: Shield, label: "Körkort", value: "AM (moped)" },
        ],
        features: [
            "Förnyat exteriör med glänsande svart ram",
            "Förnyat interiör med digitalt display och USB-port",
            "Ratt med integrerat kontrollpanel",
            "Avtagbara dörrar",
            "Elektronisk handbroms",
            "LED-belysning",
            "Uppvärmd vindruta",
            "4 skivbromsar med regenerativt bromssystem",
            "3-punkts säkerhetsbälten",
            "Maxi sidospeglar",
            "Birò App",
            "2 års garanti",
        ],
        battery: "Birò Small är utrustad med ett fast Maxi Lithium-batteri. Den 100 km räckvidden ger dig optimal rörelsefrihet. Batteriet laddas enkelt via vanligt vägguttag.",
        otherModel: { slug: "/biro-big", name: "Birò Big" },
    },
    big: {
        name: "Birò Big",
        subtitle: "Dressed Big (B2211)",
        tagline: "Kräver B-körkort – upp till 60 km/h",
        description: "Birò Big är vår modell med extra kraft och utrymme. Med en topphastighet på 60 km/h och över 300 liters lastutrymme är den perfekt för både pendling och ärenden.",
        images: [
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/9kuw3yw5_biro-classic-big-b2211_1.jpg",
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/6jyc9z1k_biro-classic-big-b2211_5.jpg",
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/1c2wp1ew_biro-classic-big-b2211_6.jpg",
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/x68rxlaa_biro-classic-small-b2211_6.jpg",
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/93h0qhy7_biro-classic-small-b2211_7.jpg",
            "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/ynvav0cp_biro-classic-small-b2211_8.jpg",
        ],
        specs: [
            { icon: Gauge, label: "Topphastighet", value: "60 km/h" },
            { icon: Battery, label: "Räckvidd", value: "Upp till 90 km" },
            { icon: Timer, label: "Laddtid", value: "3–5 timmar" },
            { icon: Ruler, label: "Längd / Bredd", value: "195 / 103 cm" },
            { icon: Weight, label: "Vikt", value: "Ca 340 kg" },
            { icon: Shield, label: "Körkort", value: "B-körkort" },
        ],
        features: [
            "Förnyat exteriör med glänsande svart ram",
            "Förnyat interiör med digitalt display och USB-port",
            "Ratt med integrerat kontrollpanel",
            "Avtagbara dörrar",
            "Elektronisk handbroms",
            "LED-belysning",
            "Uppvärmd vindruta",
            "4 skivbromsar med regenerativt bromssystem",
            "3-punkts säkerhetsbälten",
            "Maxi sidospeglar",
            "Över 300 liters lastutrymme",
            "Birò App",
            "2 års garanti",
        ],
        battery: "Birò Big är utrustad med ett fast Maxi Lithium-batteri. Den 90 km räckvidden ger dig optimal rörelsefrihet. Batteriet laddas enkelt via vanligt vägguttag.",
        otherModel: { slug: "/biro-small", name: "Birò Small" },
    },
};

function ImageGallery({ images }) {
    const [active, setActive] = useState(0);
    const prev = () => setActive((a) => (a === 0 ? images.length - 1 : a - 1));
    const next = () => setActive((a) => (a === images.length - 1 ? 0 : a + 1));

    return (
        <div data-testid="product-gallery">
            <div className="relative bg-white flex items-center justify-center overflow-hidden" style={{ height: "480px" }}>
                <img src={images[active]} alt="Birò" className="max-w-full max-h-full object-contain transition-opacity duration-300" />
                <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors" data-testid="gallery-prev">
                    <ChevronLeft size={20} />
                </button>
                <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors" data-testid="gallery-next">
                    <ChevronRight size={20} />
                </button>
            </div>
            <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                {images.map((img, i) => (
                    <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                        className={`flex-shrink-0 w-20 h-20 border-2 overflow-hidden transition-colors ${i === active ? "border-black" : "border-transparent opacity-60 hover:opacity-100"}`}>
                        <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                ))}
            </div>
        </div>
    );
}

function OfferRequestModal({ open, onOpenChange, modelName }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
    const [sending, setSending] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/offer-request", { ...form, vehicle_model: modelName });
            toast.success("Tack! Vi skickar en offert till dig inom kort.");
            setForm({ name: "", email: "", phone: "", company: "", message: "" });
            onOpenChange(false);
        } catch { toast.error("Kunde inte skicka förfrågan. Försök igen."); }
        finally { setSending(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" data-testid="offer-request-modal">
                <DialogHeader>
                    <DialogTitle className="text-xl font-medium tracking-tight">Begär offert</DialogTitle>
                    <p className="text-sm text-neutral-500 mt-1">Fyll i dina uppgifter så återkommer vi med en offert för {modelName}.</p>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3 mt-2">
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Namn</Label>
                        <Input data-testid="offer-req-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Förnamn Efternamn" required className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">E-post</Label>
                        <Input data-testid="offer-req-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="namn@exempel.se" required className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Telefon</Label>
                        <Input data-testid="offer-req-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="07X XXX XX XX" className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Företag (valfritt)</Label>
                        <Input data-testid="offer-req-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Företagsnamn" className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Meddelande (valfritt)</Label>
                        <Textarea data-testid="offer-req-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Berätta gärna vad du är intresserad av..." className="mt-1 min-h-[80px]" />
                    </div>
                    <Button data-testid="offer-req-submit" type="submit" disabled={sending} className="w-full bg-black text-white hover:bg-neutral-800 py-5 text-xs uppercase tracking-[0.15em]">
                        {sending ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
                        {sending ? "Skickar..." : "Skicka offertförfrågan"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function ProductPage({ model }) {
    const [bookingOpen, setBookingOpen] = useState(false);
    const [offerOpen, setOfferOpen] = useState(false);
    const product = PRODUCTS[model];
    if (!product) return null;

    return (
        <div className="bg-white min-h-screen" data-testid={`product-page-${model}`}>
            <Navbar onBookingClick={() => setBookingOpen(true)} />

            <div className="pt-20 md:pt-24">
                {/* Breadcrumb */}
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
                    <div className="flex items-center gap-2 text-xs text-neutral-400">
                        <Link to="/" className="hover:text-black transition-colors">Hem</Link>
                        <span>/</span>
                        <Link to="/#models" className="hover:text-black transition-colors">Modeller</Link>
                        <span>/</span>
                        <span className="text-black">{product.name}</span>
                    </div>
                </div>

                {/* Hero: Gallery + Info */}
                <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                        <ImageGallery images={product.images} />
                        <div className="flex flex-col justify-center">
                            {product.subtitle && <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">{product.subtitle}</p>}
                            <h1 className="text-4xl sm:text-5xl font-light tracking-tighter mb-3">{product.name}</h1>
                            <p className="text-sm text-neutral-500 mb-6">{product.tagline}</p>
                            <p className="text-sm text-neutral-600 leading-relaxed mb-8">{product.description}</p>

                            <div className="flex flex-col sm:flex-row gap-3 mb-8">
                                <Button data-testid="product-booking-cta" onClick={() => setBookingOpen(true)}
                                    className="bg-black text-white hover:bg-neutral-800 px-8 py-6 text-xs uppercase tracking-[0.2em]">
                                    Boka provkörning <ArrowRight size={16} className="ml-2" />
                                </Button>
                                <Button variant="outline" className="px-8 py-6 text-xs uppercase tracking-[0.2em]" data-testid="product-offer-cta"
                                    onClick={() => setOfferOpen(true)}>
                                    Begär offert
                                </Button>
                            </div>

                            <p className="text-xs text-neutral-400">Kontakta oss för aktuella priser och leasingvillkor.</p>
                        </div>
                    </div>
                </div>

                {/* Specs Grid */}
                <div className="bg-[#F5F5F5] py-16 md:py-24">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Specifikationer</p>
                        <h2 className="text-3xl sm:text-4xl font-normal tracking-tight mb-12">Teknisk data</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                            {product.specs.map((spec, i) => (
                                <div key={i} className="flex gap-4 items-start" data-testid={`spec-${i}`}>
                                    <div className="w-10 h-10 border border-neutral-300 flex items-center justify-center flex-shrink-0">
                                        <spec.icon size={18} className="text-neutral-500" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-neutral-500">{spec.label}</div>
                                        <div className="text-lg font-medium mt-0.5">{spec.value}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Standard Features */}
                <div className="py-16 md:py-24">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Standardutrustning</p>
                        <h2 className="text-3xl sm:text-4xl font-normal tracking-tight mb-12">{product.name} levereras med</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-4">
                            {product.features.map((f, i) => (
                                <div key={i} className="flex items-center gap-3 py-2 border-b border-neutral-100">
                                    <Check size={16} className="text-black flex-shrink-0" />
                                    <span className="text-sm text-neutral-700">{f}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Battery & Info sections */}
                <div className="bg-black text-white py-16 md:py-24">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                            <div>
                                <h3 className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Batteri</h3>
                                <h2 className="text-2xl sm:text-3xl font-normal tracking-tight mb-6">Maxi Lithium</h2>
                                <p className="text-sm text-neutral-300 leading-relaxed">{product.battery}</p>
                            </div>
                            <div>
                                <h3 className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Garanti & Service</h3>
                                <h2 className="text-2xl sm:text-3xl font-normal tracking-tight mb-6">2 års garanti</h2>
                                <p className="text-sm text-neutral-300 leading-relaxed mb-4">
                                    Vi levererar din Birò i perfekt skick. Skulle det mot förmodan vara något fel kan du räkna med vår omfattande fabriksgaranti. På både ram och batteri ger vi upp till 2 års garanti eller 1 000 laddcykler.
                                </p>
                                <p className="text-sm text-neutral-300 leading-relaxed">
                                    Regelbundet underhåll håller din Birò i perfekt skick. Vi rekommenderar service var 5 000 km eller 2 gånger per år.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Personalization */}
                <div className="py-16 md:py-24">
                    <div className="max-w-7xl mx-auto px-6 md:px-12 text-center max-w-3xl">
                        <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Personalisering</p>
                        <h2 className="text-3xl sm:text-4xl font-normal tracking-tight mb-6">Gör din Birò unik</h2>
                        <p className="text-sm text-neutral-500 leading-relaxed mb-8">
                            {product.name} levereras som standard med svart ramfärg. Både in- och exteriör kan helt anpassas efter dina önskemål. Kontakta oss för att diskutera möjligheterna.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button data-testid="product-cta-bottom" onClick={() => setBookingOpen(true)}
                                className="bg-black text-white hover:bg-neutral-800 px-8 py-4 text-xs uppercase tracking-[0.2em]">
                                Boka provkörning <ArrowRight size={16} className="ml-2" />
                            </Button>
                            <Link to={product.otherModel.slug}>
                                <Button variant="outline" className="px-8 py-4 text-xs uppercase tracking-[0.2em] w-full" data-testid="product-other-model">
                                    Se {product.otherModel.name}
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
            <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
            <OfferRequestModal open={offerOpen} onOpenChange={setOfferOpen} modelName={product.name} />
            <CookieConsent />
        </div>
    );
}
